import type { ColumnKey, ColumnMapping } from '@/features/network/models';

export const HEADER_MAP: Record<string, ColumnKey> = {
  Subject: 'subject',
  Status: 'status',
  'Service ID': 'serviceId',
  'Internal ID': 'internalId',
  'Service Type': 'serviceType',
  Vendor: 'vendor',
  'Supplier ID': 'supplierId',
  'Old CRM ID': 'oldCrmId',
  'Location A': 'from',
  'Location Z': 'to',
  'Type of Cross Connect': 'typeOfCrossConnect',
  'Port A Position': 'portAPosition',
  'Position Z': 'positionZ',
  'MRC Total': 'mrcTotal',
  'MRC Tax': 'mrcTax',
  MRC: 'mrc',
  'SLA RTD': 'slaRtdMs',
  NRC: 'nrc',
  ARC: 'arc',
  'Circuit type': 'circuitType',
  'Expected RTD': 'expectedRtdMs',
  Capacity: 'capacityMbps',
  'Vendor Name': 'vendorName',
};

const HEADER_SYNONYMS: Record<ColumnKey, string[]> = {
  subject: ['subject', 'name', 'title'],
  status: ['status', 'state'],
  serviceId: ['service id', 'serviceid', 'id'],
  internalId: ['internal id', 'internalid'],
  serviceType: ['service type', 'servicetype'],
  vendor: ['vendor', 'provider'],
  supplierId: ['supplier id', 'supplierid'],
  oldCrmId: ['old crm id', 'crm id'],
  from: ['location a', 'from', 'source', 'a end', 'site a'],
  to: ['location z', 'to', 'destination', 'z end', 'site z'],
  typeOfCrossConnect: ['type of cross connect', 'cross connect type'],
  portAPosition: ['port a position'],
  positionZ: ['position z', 'port z position'],
  mrcTotal: ['mrc total', 'total mrc'],
  mrcTax: ['mrc tax', 'tax'],
  mrc: ['mrc', 'monthly recurring'],
  slaRtdMs: ['sla rtd', 'sla latency', 'sla'],
  nrc: ['nrc', 'non recurring'],
  arc: ['arc'],
  circuitType: ['circuit type'],
  expectedRtdMs: ['expected rtd', 'expected latency'],
  capacityMbps: ['capacity', 'bandwidth', 'speed'],
  vendorName: ['vendor name', 'provider name'],
};

function normalizeHeader(header: string) {
  return header.trim().toLowerCase().replace(/\u00a0/g, ' ').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
}

const NORMALIZED_HEADER_MAP: Record<string, ColumnKey> = Object.fromEntries(
  Object.entries(HEADER_MAP).map(([header, key]) => [normalizeHeader(header), key]),
) as Record<string, ColumnKey>;

export const MAPPING_FIELDS: Array<{ key: ColumnKey; label: string; required?: boolean }> = [
  { key: 'from', label: 'Location A', required: true },
  { key: 'to', label: 'Location Z', required: true },
  { key: 'subject', label: 'Subject / Name' },
  { key: 'serviceId', label: 'Service ID' },
  { key: 'status', label: 'Status' },
  { key: 'vendorName', label: 'Vendor Name' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'serviceType', label: 'Service Type' },
  { key: 'circuitType', label: 'Circuit Type' },
  { key: 'mrc', label: 'MRC' },
  { key: 'nrc', label: 'NRC' },
  { key: 'slaRtdMs', label: 'SLA RTD' },
  { key: 'expectedRtdMs', label: 'Expected RTD' },
  { key: 'capacityMbps', label: 'Capacity' },
];

export function inferColumnMapping(headers: string[]): ColumnMapping {
  const result: ColumnMapping = {};

  for (const header of headers) {
    const directMatch = HEADER_MAP[header] ?? NORMALIZED_HEADER_MAP[normalizeHeader(header)];
    if (directMatch) {
      result[directMatch] = header;
      continue;
    }

    const normalized = normalizeHeader(header);
    for (const [key, synonyms] of Object.entries(HEADER_SYNONYMS) as Array<[ColumnKey, string[]]>) {
      if (result[key]) continue;
      if (synonyms.some((synonym) => normalized === synonym || normalized.includes(synonym))) {
        result[key] = header;
        break;
      }
    }
  }

  return result;
}
